/**
 * Quien hizo que en el partido: goles, asistencias y el resto de la ficha.
 *
 * La simulacion sabe que el marcador subio, pero no de quien fue el merito —
 * `state.score` es un numero. Esto lo reconstruye observando los golpeos, que
 * es la unica fuente fiable (deducirlo de posiciones falla con los rebotes).
 *
 * No toca la simulacion ni el RNG: solo escucha hooks y lee estado.
 */
import { PHYSICS, type Player, type TeamSide } from "@golazo/shared";
import { t } from "../i18n/index.js";
import type { KickKind } from "./kick.js";

/** Ventana en la que un golpeo previo todavia cuenta como asistencia. */
const ASSIST_WINDOW_TICKS = Math.round(PHYSICS.TICK_RATE * 5);
/** Un gol se atribuye al ultimo golpeo dentro de esta ventana. */
const SCORER_WINDOW_TICKS = Math.round(PHYSICS.TICK_RATE * 6);

export interface PlayerStat {
  id: string;
  side: TeamSide;
  shirtNumber: number;
  nickname: string | null;
  role: string;
  goals: number;
  assists: number;
  shots: number;
  passes: number;
  /** Pases que acabaron en un companero. */
  passesOk: number;
  /** Balones robados a un rival. */
  recoveries: number;
  /** Veces que tomo posesion del balon. */
  touches: number;
  /** Balones perdidos ante un rival. */
  losses: number;
}

export interface GoalEvent {
  side: TeamSide;
  scorerId: string | null;
  assistId: string | null;
  /** Segundo de partido en que cayo, para el resumen. */
  atSecond: number;
  /** El gol se lo metio un jugador en su propia porteria. */
  ownGoal: boolean;
}

export interface MatchStatsSnapshot {
  players: PlayerStat[];
  goals: GoalEvent[];
  /** Mejor jugador del partido, o null si no hay nada que destacar. */
  mvp: { stat: PlayerStat; score: number; reason: string } | null;
}

interface Touch {
  playerId: string;
  side: TeamSide;
  tick: number;
  kind: KickKind;
}

export interface MatchStats {
  /** Avisa de un golpeo. Se engancha a `SimHooks.onKick`. */
  recordKick(kind: KickKind, by: Player): void;
  /** Avisa de una perdida. Se engancha a `SimHooks.onPossessionLost`. */
  recordLoss(from: Player, by: Player | null): void;
  /**
   * Se llama cada tick con el estado actual. Detecta goles comparando el
   * marcador, y sigue quien tiene el balon para contar toques.
   */
  update(score: Record<TeamSide, number>, ownerId: string | null, elapsed: number): GoalEvent | null;
  snapshot(): MatchStatsSnapshot;
}

function blankStat(p: Player): PlayerStat {
  return {
    id: p.id,
    side: p.side,
    shirtNumber: p.shirtNumber,
    nickname: p.nickname,
    role: p.tacticRole,
    goals: 0,
    assists: 0,
    shots: 0,
    passes: 0,
    passesOk: 0,
    recoveries: 0,
    touches: 0,
    losses: 0,
  };
}

/**
 * Puntuacion de MVP.
 *
 * Un gol pesa mas que una asistencia, y una asistencia mas que cualquier
 * volumen de toques: si no fuera asi el MVP seria siempre el que mas corre,
 * que es justo lo contrario de lo que se quiere destacar. El resto entra como
 * desempate, no como via principal.
 */
function mvpScore(s: PlayerStat): number {
  return (
    s.goals * 100 +
    s.assists * 65 +
    s.recoveries * 6 +
    s.passesOk * 2 +
    s.shots * 3 -
    s.losses * 2
  );
}

function mvpReason(s: PlayerStat): string {
  if (s.goals >= 2) return t("{n} goles", { n: s.goals });
  if (s.goals === 1 && s.assists >= 1) return t("gol y asistencia");
  if (s.goals === 1) return t("el gol del partido");
  if (s.assists >= 2) return t("{n} asistencias", { n: s.assists });
  if (s.assists === 1) return t("la asistencia");
  if (s.recoveries >= 4) return t("{n} balones robados", { n: s.recoveries });
  return t("el mas constante");
}

/**
 * Puntuacion de un jugador en un partido. Es la columna que ordena la tabla
 * final, y tambien el record de "mejor partido" de la carrera.
 *
 * Existe porque ordenar por goles deja un partido entero de trabajo sin
 * reflejar: el que recupera diez balones y da doce pases buenos sale ultimo
 * detras de alguien que solo remato. Los pesos dicen lo que el juego valora —
 * marcar manda, pero robar y circular cuentan — y las perdidas restan, que es
 * lo que impide farmear puntos regalando el balon.
 *
 * Vive aqui, con el modelo, y no en la pantalla que la pinta: la carrera la
 * necesita para guardar records y no puede depender de la interfaz.
 */
export function playerScore(p: PlayerStat): number {
  return (
    p.goals * 100 +
    p.assists * 60 +
    p.recoveries * 15 +
    p.shots * 5 +
    p.passesOk * 3 -
    p.losses * 10
  );
}

export function createMatchStats(players: readonly Player[]): MatchStats {
  const stats = new Map<string, PlayerStat>();
  for (const p of players) stats.set(p.id, blankStat(p));

  const goals: GoalEvent[] = [];
  /** Golpeos recientes, del mas nuevo al mas viejo. */
  const recent: Touch[] = [];
  let tick = 0;
  let lastScore: Record<TeamSide, number> = { home: 0, away: 0 };
  let lastOwnerId: string | null = null;
  /** Ultimo pase sin resolver, para contar pases completados. */
  let pendingPass: { playerId: string; side: TeamSide; tick: number } | null = null;

  const get = (id: string): PlayerStat | undefined => stats.get(id);

  return {
    recordKick(kind, by): void {
      const stat = get(by.id);
      if (stat) {
        if (kind === "shoot") stat.shots++;
        else stat.passes++;
      }
      recent.unshift({ playerId: by.id, side: by.side, tick, kind });
      if (recent.length > 24) recent.pop();
      pendingPass = kind === "shoot" ? null : { playerId: by.id, side: by.side, tick };
    },

    recordLoss(from, by): void {
      const loser = get(from.id);
      if (loser) loser.losses++;
      const thief = by ? get(by.id) : undefined;
      if (thief) thief.recoveries++;
    },

    update(score, ownerId, elapsed): GoalEvent | null {
      tick++;

      // Toques y pases completados: se resuelven al cambiar de dueno.
      if (ownerId && ownerId !== lastOwnerId) {
        const stat = get(ownerId);
        if (stat) stat.touches++;
        if (
          pendingPass &&
          pendingPass.playerId !== ownerId &&
          stat &&
          stat.side === pendingPass.side &&
          tick - pendingPass.tick <= ASSIST_WINDOW_TICKS
        ) {
          const passer = get(pendingPass.playerId);
          if (passer) passer.passesOk++;
        }
        pendingPass = null;
      }
      lastOwnerId = ownerId;

      const homeUp = score.home - lastScore.home;
      const awayUp = score.away - lastScore.away;
      if (homeUp <= 0 && awayUp <= 0) {
        lastScore = { ...score };
        return null;
      }

      const side: TeamSide = homeUp > 0 ? "home" : "away";
      lastScore = { ...score };

      // El gol es del ultimo que golpeo dentro de la ventana. Si ese golpeo fue
      // de un rival, es en propia puerta.
      const last = recent.find((t) => tick - t.tick <= SCORER_WINDOW_TICKS) ?? null;
      const ownGoal = last !== null && last.side !== side;
      const scorerId = last && !ownGoal ? last.playerId : null;

      // Asistencia: el golpeo ANTERIOR, si fue un pase de un companero.
      let assistId: string | null = null;
      if (scorerId && last) {
        const prev = recent.find(
          (t) =>
            t.tick < last.tick &&
            t.side === side &&
            t.playerId !== scorerId &&
            t.kind !== "shoot" &&
            last.tick - t.tick <= ASSIST_WINDOW_TICKS,
        );
        if (prev) assistId = prev.playerId;
      }

      if (scorerId) {
        const scorer = get(scorerId);
        if (scorer) scorer.goals++;
      }
      if (assistId) {
        const assistant = get(assistId);
        if (assistant) assistant.assists++;
      }

      const event: GoalEvent = {
        side,
        scorerId,
        assistId,
        atSecond: Math.max(0, Math.round(elapsed)),
        ownGoal,
      };
      goals.push(event);
      recent.length = 0;
      return event;
    },

    snapshot(): MatchStatsSnapshot {
      /*
       * El apodo se lee AHORA, no al empezar. Se copiaba en `blankStat`, al
       * crear la ficha: quien entraba a mitad de partido salia en la tabla
       * final como "Jugador 7", y quien se iba seguia con su nombre en un
       * jugador que ya llevaba la IA. El nombre que vale es el de quien
       * termino el partido en ese sitio.
       */
      for (const stat of stats.values()) {
        stat.nickname = players.find((p) => p.id === stat.id)?.nickname ?? null;
      }
      const list = [...stats.values()];
      let mvp: MatchStatsSnapshot["mvp"] = null;
      for (const stat of list) {
        const score = mvpScore(stat);
        if (score <= 0) continue;
        if (!mvp || score > mvp.score) mvp = { stat, score, reason: mvpReason(stat) };
      }
      return { players: list, goals: [...goals], mvp };
    },
  };
}
